import * as React from 'react';

import { useUndoCancellationDataForXdp } from 'bundles/subscriptions/utils/cancellationUndoUtils';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AssignButtonNotConfigurable from 'bundles/unified-description-page-common/components/admin/AssignButtonNotConfigurable';
import ConsumerCourseEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerCourseEnrollButtonNotConfigurable';
import HeroCoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/modules/hero/HeroCoBrandingPartnerLogos';
import HeroCourseraPlusOrLiteCta from 'bundles/unified-description-page-common/components/modules/hero/HeroCourseraPlusOrLiteCta';
import HeroHorizontalModule from 'bundles/unified-description-page-common/components/modules/hero/HeroHorizontalModule';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroLanguageInfo from 'bundles/unified-description-page-common/components/modules/hero/HeroLanguageInfo';
import HeroModuleWithImage from 'bundles/unified-description-page-common/components/modules/hero/HeroModuleWithImage';
import HeroPartnerLogo from 'bundles/unified-description-page-common/components/modules/hero/HeroPartnerLogo';
import HeroTotalEnrollmentCount from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import HeroCourseDescription from 'bundles/unified-description-page-common/components/modules/hero/course/HeroCourseDescription';
import KeyInformationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalModule';
import KeyInformationSpecializationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationSpecializationHorizontalModule';
import ProductOverviewDescription from 'bundles/unified-description-page-common/components/modules/product-overview/Description';
import LearningObjectives from 'bundles/unified-description-page-common/components/modules/product-overview/LearningObjectives';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import Skills from 'bundles/unified-description-page-common/components/modules/product-overview/Skills';
import Details from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import { HERO_BACKGROUND_COLOR, MAX_NUMBER_OF_KIM_ITEMS } from 'bundles/unified-description-page-common/constants';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import AboutSubmoduleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/AboutSubmoduleRenderer';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ProductOverviewModuleConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { getConsumerCourseKeyInformationItems } from 'bundles/unified-description-page-common/utils/getConsumerKeyInformationItems';
import getCourseProductDetailItems from 'bundles/unified-description-page-common/utils/getCourseProductDetailItems';
import { COURSE_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment;
  config?: ProductOverviewModuleConfig;
};

const ProductOverviewModuleRenderer = ({ pageData, config }: Props) => {
  const { navigationLinks } = usePageNavigation();
  const { getSpecializationLink } = usePageInfo();

  const courseraPlusOwnership = useCourseraPlusOwnership();
  const { showPlusMonthlyToAnnualUpgradeButton, isCourseraPlusEligibleProduct } = courseraPlusOwnership ?? {};
  const { showUndoCancellationCTA } = useUndoCancellationDataForXdp({
    productItemId: pageData?.primarySpecialization?.id,
    isCourseraPlusEligibleProduct,
    partnerIds: pageData?.partners?.map(({ id }) => id),
  });
  const showEnrollmentCountOnReviewsItem = showPlusMonthlyToAnnualUpgradeButton || showUndoCancellationCTA;

  const productTitle = getProductTitle(pageData.name);
  const partnersToDisplay = getPartnersForCourse(pageData.partners, pageData.activeSpecialization);
  const { items: keyInformationItems } = getConsumerCourseKeyInformationItems(
    pageData,
    {
      ...config?.keyInfoModule,
    },
    config?.removeLinksAboveFold,
    MAX_NUMBER_OF_KIM_ITEMS,
    showEnrollmentCountOnReviewsItem
  );

  const showRedesignedHorizontalModule = !!config?.showBackgroundImage;

  const overviewSkills = <Skills skills={pageData.skills} disableCTA={config?.removeLinksAboveFold} />;
  const overviewDescription = pageData.cmlLearningObjectives ? (
    <LearningObjectives learningObjectives={pageData.cmlLearningObjectives} />
  ) : (
    <ProductOverviewDescription description={pageData.description} productId={pageData.id} />
  );

  const details = <Details detailItems={getCourseProductDetailItems(pageData, config?.productDetailItems || [])} />;

  const getAdditionalInfo = () => {
    if (showEnrollmentCountOnReviewsItem) {
      return null;
    }

    return <HeroTotalEnrollmentCount count={pageData.totalEnrollmentCount} />;
  };

  const getAdditionalCta = () => {
    if (showEnrollmentCountOnReviewsItem) {
      return null;
    }

    return (
      courseraPlusOwnership?.isUserEligibleToSeeCta && (
        <HeroCourseraPlusOrLiteCta
          isSubscribedToCPlusOrLite={courseraPlusOwnership.isSubscribedToCPlusOrLite}
          isSubscribedToCourseraLite={courseraPlusOwnership.isSubscribedToCourseraLite}
          hasSpecialization={Boolean(pageData.primarySpecialization)}
          partners={pageData?.partners}
          disableCTA={config?.removeLinksAboveFold}
          courseId={pageData.id}
          premiumExperienceVariant={pageData.premiumExperienceVariant}
        />
      )
    );
  };

  const HeroModuleComponent = showRedesignedHorizontalModule ? HeroModuleWithImage : HeroHorizontalModule;
  const KeyInformationHorizontalComponent = showRedesignedHorizontalModule
    ? KeyInformationSpecializationHorizontalModule
    : KeyInformationHorizontalModule;
  return (
    <ProductOverviewHorizontalModule
      anchorId={COURSE_NAV_ITEMS.ABOUT}
      heroBackgroundColor={HERO_BACKGROUND_COLOR}
      navigationLinks={navigationLinks}
      hero={
        <HeroModuleComponent
          productTitle={productTitle}
          {...(showRedesignedHorizontalModule && {
            showBackgroundImage: true,
            backgroundImage: config?.backgroundImage,
          })}
          partnerLogo={
            partnersToDisplay?.length === 2 ? (
              <HeroCoBrandingPartnerLogos
                // @ts-expect-error URL: string
                leftImage={partnersToDisplay[0].primaryLogo}
                leftPartnerName={partnersToDisplay[0].name}
                // @ts-expect-error URL: string
                rightImage={partnersToDisplay[1].primaryLogo}
                rightPartnerName={partnersToDisplay[1].name}
              />
            ) : (
              // @ts-expect-error URL: string
              <HeroPartnerLogo image={partnersToDisplay?.[0].primaryLogo} partnerName={partnersToDisplay?.[0].name} />
            )
          }
          description={
            <React.Fragment>
              {pageData.primarySpecialization && (
                <HeroCourseDescription
                  primarySpecialization={pageData.primarySpecialization}
                  primarySpecializationLink={getSpecializationLink({
                    productSlug: pageData.primarySpecialization.slug,
                    productVariant: pageData.primarySpecialization.productVariant,
                  })}
                  specializations={pageData.specializations}
                />
              )}
              {config?.showHeroLanguageInfo && (
                <HeroLanguageInfo
                  languages={{
                    primaryLanguages: pageData.primaryLanguages,
                    subtitleLanguages: pageData.subtitleLanguages ?? [],
                    translatedLanguages: pageData.translatedLanguages ?? [],
                  }}
                />
              )}
            </React.Fragment>
          }
          instructors={
            <HeroInstructors
              instructors={pageData.instructors}
              averageInstructorRating={pageData.averageInstructorRating}
              instructorRatingCount={pageData.instructorRatingCount}
              partners={pageData.partners}
              disableCTA={config?.removeLinksAboveFold}
              displayTopInstructorBadgeInline
            />
          }
          primaryCta={
            <ConsumerCourseEnrollButtonNotConfigurable
              pageData={pageData}
              size="large"
              hideFinaidLink={config?.removeLinksAboveFold}
              onlyShowButton={config?.showAssignButton || showEnrollmentCountOnReviewsItem}
            />
          }
          secondaryCta={
            config?.showAssignButton && <AssignButtonNotConfigurable productSlug={pageData.slug} productType="course" />
          }
          additionalInfo={getAdditionalInfo()}
          additionalCta={getAdditionalCta()}
        />
      }
      keyInformation={<KeyInformationHorizontalComponent keyInformationItems={keyInformationItems} />}
      aboutSubmodule={
        <AboutSubmoduleRenderer
          description={overviewDescription}
          skills={overviewSkills}
          details={details}
          config={config}
        />
      }
    />
  );
};

export default ProductOverviewModuleRenderer;
